import { useEffect, useState, useCallback } from "react";
import { usePortalNavigate } from "../hooks/usePortalNavigate";
import { FileText, Folder, Link2 } from "lucide-react";
import { theme, card, emptyFrame } from "../theme";
import { DimensionHeading } from "../components/Drawing";
import { api } from "../api/client";
import { generatePolicyDocument } from "../lib/generatePolicy";
import { fmtDateOnly } from "../lib/dateOnly";
import { safeHref, openSafe } from "../utils/safeHref";
import { MarkdownDocument } from "../components/MarkdownDocument";
import { PortalLink } from "../components/PortalLink";
import { useCurrentUser } from "../auth/useCurrentUser";
import { useFrameworks } from "../context/FrameworksContext";

import { cbSafe } from "../lib/colourBlind";
// ─── Types ────────────────────────────────────────────────────────────────────

interface ControlTag {
  frameworkId: string;
  controlRef: string;
}

interface DocVersion {
  id: string;
  versionNumber: number;
  generatedAt: string;
  staleSince: string | null;
  staleReason?: string | null;
  controls: ControlTag[];
  content?: string;        // lazily loaded markdown
  loadingContent?: boolean;
  contentError?: boolean;  // the content could not be fetched
}

interface GeneratedDoc {
  id: string;
  versionNumber: number;
  generatedAt: string;
  staleSince: string | null;
  staleReason?: string | null;
  controls: ControlTag[];
}

interface Template {
  id: string;
  title: string;
  category: string;
  description: string;
  /** "docx" for every policy and notice; "xlsx" for row-based registers (the RoPA). */
  fileFormat?: "docx" | "xlsx";
  frameworkIds?: string[];
  regulations?: string[];
}

interface PolicyEntry {
  template: Template;
  latestDoc: GeneratedDoc;
  content: string | null;  // the latest version's markdown, fetched when the preview is first opened
  loadingContent: boolean;
  contentError: boolean;
  versions: DocVersion[];
  versionsLoaded: boolean;
  historyOpen: boolean;
  previewOpen: boolean;
  downloadingId: string | null;
  confirmDeleteDocId: string | null;
  deletingDocId: string | null;
  regenerating: boolean;
  viewingDocId: string | null;   // which version is expanded for inline preview
  comparingDocId: string | null; // which older version is being diffed against its successor
}

interface StorageStatus {
  provider: string | null;
  displayName: string | null;
  folderWebUrl: string | null;
  folderName: string | null;
  sasExpiry: string | null;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const CATEGORY_ORDER = [
  "ISO 27001 — Governance",
  "ISO 27001 — Organisational",
  "ISO 27001 — People",
  "ISO 27001 — Physical",
  "ISO 27001 — Technological",
  "Cyber Essentials",
  "ISO 42001",
  "ISO 9001",
];

const CATEGORY_FRAMEWORK: Record<string, string> = {
  "ISO 27001 — Governance":     "ISO 27001:2022",
  "ISO 27001 — Organisational": "ISO 27001:2022",
  "ISO 27001 — People":         "ISO 27001:2022",
  "ISO 27001 — Physical":       "ISO 27001:2022",
  "ISO 27001 — Technological":  "ISO 27001:2022",
  "Cyber Essentials":           "Cyber Essentials / CE+",
  "ISO 42001":                  "ISO/IEC 42001:2023",
  "ISO 9001":                   "ISO 9001:2015",
  "ISO 45001 — Occupational Health & Safety": "ISO 45001:2018",
  "ISO 14001 — Environmental":  "ISO 14001:2015",
};

const FRAMEWORK_ACCENT: Record<string, string> = {
  "ISO 27001:2022":         "#2563EB",
  "Cyber Essentials / CE+": "#0d9488",
  "ISO/IEC 42001:2023":     "#7c3aed",
  "ISO 9001:2015":          "#b45309",
  "Data Protection":        "#db2777",
};

// Data protection documents are seeded under several "Data Protection — …" categories
// but read as one library here.
function groupLabel(category: string): string {
  if (CATEGORY_FRAMEWORK[category]) return CATEGORY_FRAMEWORK[category];
  return category.startsWith("Data Protection") ? "Data Protection" : category;
}

function fmtDateTime(iso: string) {
  return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

// ─── Line diff ───────────────────────────────────────────────────────────────

type DiffLine = { type: "same" | "add" | "remove"; text: string };

function diffLines(oldText: string, newText: string): DiffLine[] {
  const a = oldText.split("\n");
  const b = newText.split("\n");
  // Myers LCS-based diff (simplified patience variant)
  const m = a.length, n = b.length;
  const dp: number[][] = Array.from({ length: m + 1 }, () => new Array(n + 1).fill(0));
  for (let i = m - 1; i >= 0; i--)
    for (let j = n - 1; j >= 0; j--)
      dp[i][j] = a[i] === b[j] ? dp[i + 1][j + 1] + 1 : Math.max(dp[i + 1][j], dp[i][j + 1]);

  const result: DiffLine[] = [];
  let i = 0, j = 0;
  while (i < m || j < n) {
    if (i < m && j < n && a[i] === b[j]) {
      result.push({ type: "same", text: a[i++] });
      j++;
    } else if (j < n && (i >= m || dp[i + 1]?.[j] <= dp[i]?.[j + 1])) {
      result.push({ type: "add", text: b[j++] });
    } else {
      result.push({ type: "remove", text: a[i++] });
    }
  }
  return result;
}

function DiffView({ oldContent, newContent, oldLabel, newLabel }: {
  oldContent: string; newContent: string; oldLabel: string; newLabel: string;
}) {
  const lines = diffLines(oldContent, newContent);
  const added   = lines.filter((l) => l.type === "add").length;
  const removed = lines.filter((l) => l.type === "remove").length;

  return (
    <div>
      <div style={{ display: "flex", gap: 12, alignItems: "center", marginBottom: 8, flexWrap: "wrap" }}>
        <span style={{ fontSize: 11, color: theme.color.subtle }}>
          Comparing <strong>{oldLabel}</strong> → <strong>{newLabel}</strong>
        </span>
        <span style={{ fontSize: 11, color: cbSafe("#16a34a"), fontWeight: 600 }}>+{added} added</span>
        <span style={{ fontSize: 11, color: cbSafe("#dc2626"), fontWeight: 600 }}>−{removed} removed</span>
      </div>
      <div style={{
        fontFamily: "monospace", fontSize: 11.5, lineHeight: 1.6,
        border: `1px solid ${theme.color.border}`, borderRadius: 3, overflowX: "auto",
        maxHeight: 480, overflowY: "auto",
      }}>
        {lines.map((line, i) => {
          const bg    = line.type === "add" ? cbSafe("#f0fdf4") : line.type === "remove" ? "#fef2f2" : "transparent";
          const color = line.type === "add" ? cbSafe("#15803d") : line.type === "remove" ? cbSafe("#b91c1c") : theme.color.subtle;
          const prefix = line.type === "add" ? "+" : line.type === "remove" ? "−" : " ";
          return (
            <div key={i} style={{ display: "flex", background: bg, minHeight: 20 }}>
              <span style={{ width: 20, flexShrink: 0, textAlign: "center", color, userSelect: "none", opacity: 0.7 }}>{prefix}</span>
              <span style={{ color, whiteSpace: "pre-wrap", wordBreak: "break-word", flex: 1, paddingRight: 8 }}>{line.text}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Component ────────────────────────────────────────────────────────────────

export function PoliciesPage() {
  const navigate = usePortalNavigate();
  const [entries,    setEntries]    = useState<PolicyEntry[]>([]);
  const [loading,    setLoading]    = useState(true);
  const [storage,    setStorage]    = useState<StorageStatus | null>(null);
  // The templates that apply to this organisation (its standards, or a data protection law in its
  // locations), to say how many are still to generate — never the whole catalogue (AUD-25).
  const [catalogue, setCatalogue] = useState<{ templates: Template[]; orgRegulations: string[] } | null>(null);
  const { enrolled } = useFrameworks();

  // Same rule as Policy Templates: a consultant regenerates for a client only while the client
  // allows it (My Organisation → Consultant access). The API enforces this; the page only
  // avoids offering a refused button.
  const { user } = useCurrentUser();
  const isConsultant = user?.role === "consultant";
  const [consultantDocGen, setConsultantDocGen] = useState<{ allowed: boolean } | null>(null);
  useEffect(() => {
    if (!isConsultant) return;
    api.get<{ allowed: boolean }>("/organizations/consultant-docgen")
      .then(setConsultantDocGen)
      .catch(() => setConsultantDocGen({ allowed: false }));
  }, [isConsultant]);
  // Generating writes a document version, which the API allows only with documents:write (DOC-19).
  const canGenerate = !!user?.permissions?.includes("documents:write") && (user?.role === "client" || (isConsultant && !!consultantDocGen?.allowed));

  const patchEntry = useCallback((templateId: string, patch: Partial<PolicyEntry>) => {
    setEntries((prev) => prev.map((e) => e.template.id === templateId ? { ...e, ...patch } : e));
  }, []);

  useEffect(() => {
    api.get<StorageStatus>("/storage/status").then(setStorage).catch(() => {});

    api.get<{ templates: Template[]; orgRegulations?: string[] }>("/templates")
      .then(async ({ templates, orgRegulations }) => {
        setCatalogue({ templates, orgRegulations: orgRegulations ?? [] });

        // The latest generated version of every template in one request, without content: the
        // preview fetches a document's markdown when it is first opened.
        const { documents } = await api.get<{ documents: Array<GeneratedDoc & { templateId: string }> }>("/templates/generated/latest");
        const latestByTemplate = new Map(documents.map(({ templateId, ...doc }) => [templateId, doc]));

        const generated: PolicyEntry[] = [];
        for (const tpl of templates) {
          const doc = latestByTemplate.get(tpl.id);
          if (doc) {
            generated.push({
              template:      tpl,
              latestDoc:     doc,
              content:       null,
              loadingContent: false,
              contentError:   false,
              versions:           [],
              versionsLoaded:     false,
              historyOpen:        false,
              previewOpen:        false,
              downloadingId:      null,
              viewingDocId:       null,
              comparingDocId:     null,
              confirmDeleteDocId: null,
              deletingDocId:      null,
              regenerating:       false,
            });
          }
        }

        // Sort by category order, then by title
        generated.sort((a, b) => {
          const ai = CATEGORY_ORDER.indexOf(a.template.category);
          const bi = CATEGORY_ORDER.indexOf(b.template.category);
          if (ai !== bi) return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi);
          return a.template.title.localeCompare(b.template.title);
        });

        setEntries(generated);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  async function loadVersions(templateId: string) {
    const { versions } = await api.get<{ versions: DocVersion[] }>(`/templates/${templateId}/versions`);
    patchEntry(templateId, { versions, versionsLoaded: true, historyOpen: true });
  }

  function patchVersion(templateId: string, docId: string, patch: Partial<DocVersion>) {
    setEntries((prev) => prev.map((e) =>
      e.template.id !== templateId ? e : {
        ...e, versions: e.versions.map((v) => v.id === docId ? { ...v, ...patch } : v),
      }
    ));
  }

  async function loadVersionContent(templateId: string, docId: string) {
    patchVersion(templateId, docId, { loadingContent: true, contentError: false });
    try {
      const { content } = await api.get<{ content: string }>(`/templates/generated/${docId}/content`);
      patchVersion(templateId, docId, { content, loadingContent: false });
    } catch {
      patchVersion(templateId, docId, { loadingContent: false, contentError: true });
    }
  }

  async function toggleVersionView(templateId: string, docId: string) {
    const entry = entries.find((e) => e.template.id === templateId);
    if (!entry) return;
    const isOpen = entry.viewingDocId === docId;
    patchEntry(templateId, { viewingDocId: isOpen ? null : docId, comparingDocId: null });
    if (!isOpen) {
      const ver = entry.versions.find((v) => v.id === docId);
      if (ver && !ver.content) await loadVersionContent(templateId, docId);
    }
  }

  async function toggleVersionCompare(templateId: string, docId: string) {
    const entry = entries.find((e) => e.template.id === templateId);
    if (!entry) return;
    const isOpen = entry.comparingDocId === docId;
    patchEntry(templateId, { comparingDocId: isOpen ? null : docId, viewingDocId: null });
    if (!isOpen) {
      // Need content for this version and the next (successor) version
      const sorted = [...entry.versions].sort((a, b) => a.versionNumber - b.versionNumber);
      const idx = sorted.findIndex((v) => v.id === docId);
      const toLoad = [docId, sorted[idx + 1]?.id].filter((id): id is string => !!id);
      for (const id of toLoad) {
        const ver = entry.versions.find((v) => v.id === id);
        if (ver && !ver.content) await loadVersionContent(templateId, id);
      }
    }
  }

  async function toggleHistory(templateId: string) {
    const entry = entries.find((e) => e.template.id === templateId);
    if (!entry) return;
    if (!entry.versionsLoaded) {
      await loadVersions(templateId);
    } else {
      patchEntry(templateId, { historyOpen: !entry.historyOpen });
    }
  }

  async function deleteVersion(templateId: string, docId: string) {
    patchEntry(templateId, { deletingDocId: docId, confirmDeleteDocId: null });
    try {
      await api.del(`/templates/generated/${docId}`);
      const entry = entries.find((e) => e.template.id === templateId);
      if (!entry) return;
      const remaining = entry.versions.filter((v) => v.id !== docId);
      if (remaining.length === 0) {
        // All versions deleted — remove from list entirely by resetting versionsLoaded
        patchEntry(templateId, { versions: [], versionsLoaded: false, historyOpen: false, deletingDocId: null });
      } else {
        const newLatest = remaining.reduce((a, b) => a.versionNumber > b.versionNumber ? a : b);
        patchEntry(templateId, { versions: remaining, latestDoc: { ...newLatest, staleSince: null }, deletingDocId: null });
      }
    } catch {
      patchEntry(templateId, { deletingDocId: null });
    }
  }

  async function downloadDocx(templateId: string, docId: string) {
    patchEntry(templateId, { downloadingId: docId });
    try {
      const { url } = await api.get<{ url: string }>(`/templates/generated/${docId}/download-url`);
      const a = document.createElement("a");
      a.href = url;
      a.download = "";
      a.click();
    } catch { /* transient SAS errors — silent */ } finally {
      patchEntry(templateId, { downloadingId: null });
    }
  }

  // The latest version's markdown, fetched the first time its preview is opened.
  async function togglePreview(entry: PolicyEntry) {
    const opening = !entry.previewOpen;
    patchEntry(entry.template.id, { previewOpen: opening });
    if (!opening || entry.content !== null || entry.loadingContent) return;
    const docId = entry.latestDoc.id;
    patchEntry(entry.template.id, { loadingContent: true, contentError: false });
    try {
      const { content } = await api.get<{ content: string }>(`/templates/generated/${docId}/content`);
      // A regeneration while the request was out has already put the newer version's content in place.
      setEntries((prev) => prev.map((e) => e.template.id === entry.template.id && e.latestDoc.id === docId
        ? { ...e, content, loadingContent: false } : e));
    } catch {
      patchEntry(entry.template.id, { loadingContent: false, contentError: true });
    }
  }

  async function regenerate(templateId: string) {
    patchEntry(templateId, { regenerating: true });
    try {
      const { document: doc, content } = await generatePolicyDocument<GeneratedDoc>(templateId);
      patchEntry(templateId, { regenerating: false, latestDoc: doc, content, contentError: false, versionsLoaded: false, historyOpen: false, viewingDocId: null, comparingDocId: null });
    } catch {
      patchEntry(templateId, { regenerating: false });
    }
  }

  // Group entries by framework label
  const grouped: Record<string, PolicyEntry[]> = {};
  const seenFrameworks = new Set<string>();
  for (const e of entries) {
    const fw = groupLabel(e.template.category);
    grouped[fw] ??= [];
    grouped[fw].push(e);
  }

  const generatedIds = new Set(entries.map((e) => e.template.id));
  const ceEnrolled = enrolled.has("cyber_essentials") || enrolled.has("cyber_essentials_plus");
  const applies = (t: Template) => (t.regulations?.length
    ? t.regulations.some((r) => catalogue?.orgRegulations.includes(r))
    : (t.frameworkIds ?? []).some((f) => enrolled.has(f as never) || (f === "cyber_essentials" && ceEnrolled) || (f === "pci_dss" && [...enrolled].some((e) => e.startsWith("pci_dss")))));
  const ungeneratedCount = (catalogue?.templates ?? []).filter((t) => applies(t) && !generatedIds.has(t.id)).length;

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <div className="page-pad">
      {/* Header */}
      <div style={{ marginBottom: 20 }}>
        <h1 style={{ margin: "0 0 4px", fontSize: 20, fontWeight: 700, color: theme.color.heading, letterSpacing: "-0.3px" }}>
          Your policies
        </h1>
        <p style={{ margin: 0, fontSize: 13, color: theme.color.subtle }}>
          Your organisation's generated policy documents. Each policy is versioned — regenerating creates a new version and retains the history.
          {ungeneratedCount > 0 && !loading && (
            <> &nbsp;<PortalLink to="/templates" style={{ color: theme.color.accent, textDecoration: "none", fontWeight: 600 }}>
              {ungeneratedCount} template{ungeneratedCount !== 1 ? "s" : ""} not yet generated →
            </PortalLink></>
          )}
        </p>
      </div>

      {/* Cloud storage banner */}
      {storage && (
        <div style={{
          ...card,
          marginBottom: 24,
          display: "flex",
          alignItems: "center",
          gap: 14,
          background: storage.provider ? theme.color.accentBg : theme.color.panel,
          borderColor: storage.provider ? theme.color.accent : theme.color.border,
          padding: "12px 16px",
        }}>
          <div style={{ flexShrink: 0, color: storage.provider ? theme.color.accentText : theme.color.subtle }}>
            {storage.provider ? <Folder size={20} /> : <Link2 size={20} />}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            {storage.provider ? (
              <>
                <div style={{ fontSize: 12, fontWeight: 700, color: theme.color.accentText, marginBottom: 2 }}>
                  Linked to cloud storage — {storage.folderName}
                </div>
                <div style={{ fontSize: 11, color: theme.color.muted }}>
                  Generated policies are automatically synced to your storage destination.
                  {storage.displayName && <> Connected as <strong>{storage.displayName}</strong>.</>}
                </div>
              </>
            ) : (
              <>
                <div style={{ fontSize: 12, fontWeight: 700, color: theme.color.heading, marginBottom: 2 }}>
                  Connect cloud storage to sync this repository
                </div>
                <div style={{ fontSize: 11, color: theme.color.subtle }}>
                  Every policy you generate will automatically appear in your chosen storage destination, ready for auditors.
                </div>
              </>
            )}
          </div>
          {storage.provider && storage.folderWebUrl ? (
            <a
              href={safeHref(storage.folderWebUrl)}
              target="_blank"
              rel="noreferrer"
              style={{
                fontSize: 12, fontWeight: 600, padding: "6px 14px", borderRadius: 3, textDecoration: "none",
                background: theme.color.accent, color: "#fff", flexShrink: 0, whiteSpace: "nowrap",
              }}
            >
              Open folder →
            </a>
          ) : !storage.provider ? (
            <a
              href="#"
              onClick={(e) => { e.preventDefault(); navigate("/connections?tab=microsoft"); }}
              style={{
                fontSize: 12, fontWeight: 600, padding: "6px 14px", borderRadius: 3, textDecoration: "none",
                background: theme.color.accent, color: "#fff", flexShrink: 0, whiteSpace: "nowrap",
              }}
            >
              Connect storage →
            </a>
          ) : null}
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div style={{ padding: 48, textAlign: "center", color: theme.color.subtle, fontSize: 13 }}>
          Loading repository…
        </div>
      )}

      {/* Empty state */}
      {!loading && entries.length === 0 && (
        <div style={{ ...emptyFrame, padding: 48 }}>
          <div style={{ marginBottom: 12, display: "flex", justifyContent: "center" }}><FileText size={36} color="var(--c-muted)" /></div>
          <div style={{ fontSize: 14, fontWeight: 700, color: theme.color.heading, marginBottom: 6 }}>
            No policies generated yet
          </div>
          <div style={{ fontSize: 13, color: theme.color.subtle, marginBottom: 20 }}>
            Visit Policy Templates to generate your first policy document.
          </div>
          <PortalLink
            to="/templates"
            style={{
              display: "inline-block", textDecoration: "none",
              background: theme.color.accent, color: "#fff",
              padding: "8px 20px", borderRadius: 3, fontSize: 13, fontWeight: 600,
            }}
          >
            Go to Policy Templates →
          </PortalLink>
        </div>
      )}

      {/* Policy library grouped by framework */}
      {!loading && Object.entries(grouped).map(([framework, fwEntries]) => {
        const accent = FRAMEWORK_ACCENT[framework] ?? theme.color.accent;
        const isNewFramework = !seenFrameworks.has(framework);
        seenFrameworks.add(framework);

        return (
          <div key={framework}>
            {isNewFramework && (
              <DimensionHeading as="h3" count={`${fwEntries.length} document${fwEntries.length !== 1 ? "s" : ""}`} style={{ margin: "20px 0 12px" }}>
                <span style={{ display: "inline-flex", alignItems: "center", gap: 10 }}>
                  <span style={{ width: 4, height: 20, borderRadius: 2, background: accent }} />
                  {framework}
                </span>
              </DimensionHeading>
            )}

            <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 8 }}>
              {fwEntries.map((entry) => {
                const { template, latestDoc, content } = entry;
                const subcategory = template.category.includes("—")
                  ? template.category.split("—")[1].trim()
                  : "";
                const isStale = !!latestDoc.staleSince;

                return (
                  <div
                    key={template.id}
                    style={{ ...card, padding: 0, overflow: "hidden" }}
                  >
                    {/* Main row */}
                    <div style={{ padding: "12px 16px", display: "flex", alignItems: "flex-start", gap: 12 }}>
                      {/* Left: document icon + version */}
                      <div style={{
                        width: 36, height: 36, borderRadius: 3, flexShrink: 0,
                        background: `${accent}18`, border: `1px solid ${accent}33`,
                        display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
                      }}>
                        <FileText size={14} color="var(--c-accentText)" />
                      </div>

                      {/* Centre: title + meta */}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 2 }}>
                          {subcategory && (
                            <span style={{ fontSize: 10, fontWeight: 600, color: accent, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                              {subcategory}
                            </span>
                          )}
                        </div>
                        <div style={{ fontSize: 13, fontWeight: 700, color: theme.color.heading, marginBottom: 3 }}>
                          {template.title}
                        </div>
                        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                          <span style={{
                            fontSize: 10, fontWeight: 700, padding: "1px 7px", borderRadius: 3,
                            background: `${accent}18`, color: accent, border: `1px solid ${accent}33`,
                          }}>
                            v{latestDoc.versionNumber}
                          </span>
                          <span style={{ fontSize: 11, color: theme.color.faint }}>
                            Generated {fmtDateOnly(latestDoc.generatedAt)}
                          </span>
                          {isStale && (
                            <span style={{
                              fontSize: 10, fontWeight: 600, padding: "1px 7px", borderRadius: 3,
                              background: theme.color.warningBg, color: theme.color.warningText, border: `1px solid ${theme.color.warning}`,
                            }}>
                              {latestDoc.staleReason === "template_updated" ? "Template improved — regenerate" : "Stale — controls have changed"}
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Right: actions */}
                      <div style={{ display: "flex", gap: 6, alignItems: "center", flexShrink: 0, flexWrap: "wrap" }}>
                        <button
                          onClick={() => void togglePreview(entry)}
                          style={{
                            fontSize: 11, padding: "5px 12px", borderRadius: 3, cursor: "pointer",
                            background: entry.previewOpen ? theme.color.accentBg : theme.color.panel,
                            color: entry.previewOpen ? theme.color.accentText : theme.color.muted,
                            border: `1px solid ${entry.previewOpen ? theme.color.accent : theme.color.border}`,
                          }}
                        >
                          {entry.previewOpen ? "Hide" : "Preview"}
                        </button>

                        <button
                          onClick={() => downloadDocx(template.id, latestDoc.id)}
                          disabled={entry.downloadingId === latestDoc.id}
                          style={{
                            fontSize: 11, padding: "5px 12px", borderRadius: 3, cursor: "pointer",
                            background: theme.color.panel, color: theme.color.muted,
                            border: `1px solid ${theme.color.border}`,
                            opacity: entry.downloadingId === latestDoc.id ? 0.6 : 1,
                          }}
                        >
                          {entry.downloadingId === latestDoc.id ? "…" : `⬇ .${template.fileFormat ?? "docx"}`}
                        </button>

                        <button
                          onClick={() => toggleHistory(template.id)}
                          style={{
                            fontSize: 11, padding: "5px 12px", borderRadius: 3, cursor: "pointer",
                            background: entry.historyOpen ? theme.color.accentBg : theme.color.panel,
                            color: entry.historyOpen ? theme.color.accentText : theme.color.muted,
                            border: `1px solid ${entry.historyOpen ? theme.color.accent : theme.color.border}`,
                          }}
                        >
                          History {latestDoc.versionNumber > 1 ? `(${latestDoc.versionNumber})` : ""}
                        </button>

                        {canGenerate && <button
                          onClick={() => regenerate(template.id)}
                          disabled={entry.regenerating}
                          style={{
                            fontSize: 11, padding: "5px 12px", borderRadius: 3, cursor: "pointer",
                            background: theme.color.panel, color: theme.color.muted,
                            border: `1px solid ${theme.color.border}`,
                            opacity: entry.regenerating ? 0.6 : 1,
                          }}
                        >
                          {entry.regenerating ? "Regenerating…" : "Regenerate"}
                        </button>}
                      </div>
                    </div>

                    {/* Preview panel */}
                    {entry.previewOpen && !content && (entry.loadingContent || entry.contentError) && (
                      <div style={{ borderTop: `1px solid ${theme.color.border}`, padding: "12px 18px", fontSize: 12, color: theme.color.faint, background: theme.color.panelAlt }}>
                        {entry.contentError ? "Could not load the preview. Try again, or download the document." : "Loading preview…"}
                      </div>
                    )}
                    {entry.previewOpen && content && (
                      <div style={{
                        borderTop: `1px solid ${theme.color.border}`,
                        padding: "16px 18px",
                        maxHeight: 420,
                        overflowY: "auto",
                        background: theme.color.panelAlt,
                      }}>
                        <MarkdownDocument content={content} />
                      </div>
                    )}

                    {/* Version history panel */}
                    {entry.historyOpen && (() => {
                      const sortedVersions = [...entry.versions].sort((a, b) => b.versionNumber - a.versionNumber);
                      return (
                        <div style={{ borderTop: `1px solid ${theme.color.border}`, background: theme.color.panelAlt }}>
                          <div style={{
                            padding: "8px 16px 6px",
                            fontSize: 10, fontWeight: 700, color: theme.color.faint,
                            textTransform: "uppercase", letterSpacing: "0.06em",
                          }}>
                            Version history · {entry.versions.length} version{entry.versions.length !== 1 ? "s" : ""}
                          </div>
                          {entry.versions.length === 0 && (
                            <div style={{ padding: "8px 16px 12px", fontSize: 12, color: theme.color.subtle }}>
                              Only one version exists.
                            </div>
                          )}
                          {sortedVersions.map((v, idx) => {
                            const isCurrent = v.versionNumber === latestDoc.versionNumber;
                            const isViewing  = entry.viewingDocId  === v.id;
                            const isComparing = entry.comparingDocId === v.id;
                            // Successor = next-higher version (for compare: old → new)
                            const successorVersion = sortedVersions[idx - 1]; // sorted desc, so prev idx = higher version
                            const canCompare = !!successorVersion;

                            return (
                              <div key={v.id}>
                                {/* Row */}
                                <div style={{
                                  display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap",
                                  padding: "9px 16px",
                                  borderTop: `1px solid ${theme.color.border}`,
                                  background: isCurrent ? `${accent}08` : "transparent",
                                }}>
                                  <span style={{ fontSize: 11, fontWeight: 700, color: accent, minWidth: 28, fontVariantNumeric: "tabular-nums" }}>
                                    v{v.versionNumber}
                                  </span>
                                  <span style={{ fontSize: 11, color: theme.color.subtle, flex: 1, minWidth: 120 }}>
                                    {fmtDateTime(v.generatedAt)}
                                  </span>
                                  {isCurrent && (
                                    <span style={{ fontSize: 10, padding: "1px 6px", borderRadius: 3, background: `${accent}18`, color: accent, border: `1px solid ${accent}33` }}>
                                      Current
                                    </span>
                                  )}
                                  {v.staleSince && !isCurrent && (
                                    <span style={{ fontSize: 10, padding: "1px 6px", borderRadius: 3, background: theme.color.warningBg, color: theme.color.warningText }}>
                                      Superseded
                                    </span>
                                  )}
                                  {v.controls.length > 0 && (
                                    <span style={{ fontSize: 10, color: theme.color.faint }}>{v.controls.length} controls</span>
                                  )}
                                  {/* View toggle */}
                                  <button
                                    onClick={() => toggleVersionView(template.id, v.id)}
                                    disabled={v.loadingContent}
                                    style={{
                                      fontSize: 11, padding: "3px 10px", borderRadius: 3, cursor: "pointer",
                                      border: `1px solid ${isViewing ? accent : theme.color.border}`,
                                      background: isViewing ? `${accent}18` : "transparent",
                                      color: isViewing ? accent : theme.color.muted,
                                      opacity: v.loadingContent ? 0.5 : 1,
                                    }}
                                  >
                                    {v.loadingContent ? "…" : isViewing ? "Hide" : "View"}
                                  </button>
                                  {/* Compare toggle */}
                                  {canCompare && (
                                    <button
                                      onClick={() => toggleVersionCompare(template.id, v.id)}
                                      style={{
                                        fontSize: 11, padding: "3px 10px", borderRadius: 3, cursor: "pointer",
                                        border: `1px solid ${isComparing ? "#7c3aed" : theme.color.border}`,
                                        background: isComparing ? "#f5f3ff" : "transparent",
                                        color: isComparing ? "#7c3aed" : theme.color.muted,
                                      }}
                                    >
                                      {isComparing ? "Hide diff" : `Diff v${v.versionNumber}→v${successorVersion.versionNumber}`}
                                    </button>
                                  )}
                                  {/* Download */}
                                  <button
                                    onClick={() => downloadDocx(template.id, v.id)}
                                    disabled={entry.downloadingId === v.id}
                                    style={{
                                      fontSize: 11, padding: "3px 10px", borderRadius: 3, cursor: "pointer",
                                      border: `1px solid ${theme.color.border}`,
                                      background: "transparent", color: theme.color.muted,
                                      opacity: entry.downloadingId === v.id ? 0.5 : 1,
                                    }}
                                  >
                                    ⬇ .{template.fileFormat ?? "docx"}
                                  </button>
                                  {/* Delete */}
                                  {entry.confirmDeleteDocId === v.id ? (
                                    <span style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 11 }}>
                                      <span style={{ color: theme.color.danger }}>Delete?</span>
                                      <button onClick={() => deleteVersion(template.id, v.id)} disabled={entry.deletingDocId === v.id} style={{ background: "none", border: "none", color: theme.color.danger, fontWeight: 700, cursor: "pointer", fontSize: 11 }}>Yes</button>
                                      <button onClick={() => patchEntry(template.id, { confirmDeleteDocId: null })} style={{ background: "none", border: "none", color: theme.color.muted, cursor: "pointer", fontSize: 11 }}>No</button>
                                    </span>
                                  ) : (
                                    <button
                                      onClick={() => patchEntry(template.id, { confirmDeleteDocId: v.id })}
                                      disabled={entry.deletingDocId === v.id}
                                      style={{ background: "none", border: "none", fontSize: 11, color: theme.color.danger, cursor: "pointer", padding: "3px 6px" }}
                                    >
                                      Delete
                                    </button>
                                  )}
                                </div>

                                {/* Inline markdown preview */}
                                {isViewing && v.content && (
                                  <div style={{ padding: "12px 20px 16px", borderTop: `1px solid ${theme.color.border}`, background: theme.color.panel }}>
                                    <MarkdownDocument content={v.content} />
                                  </div>
                                )}
                                {isViewing && !v.content && v.contentError && (
                                  <div role="alert" style={{ padding: "8px 20px", fontSize: 12, color: theme.color.dangerText }}>
                                    This version's content could not be loaded. Close it and try again.
                                  </div>
                                )}

                                {/* Diff view */}
                                {isComparing && v.content && successorVersion?.content && (
                                  <div style={{ padding: "12px 20px 16px", borderTop: `1px solid ${theme.color.border}`, background: theme.color.panel }}>
                                    <DiffView
                                      oldContent={v.content}
                                      newContent={successorVersion.content}
                                      oldLabel={`v${v.versionNumber}`}
                                      newLabel={`v${successorVersion.versionNumber}`}
                                    />
                                  </div>
                                )}
                                {isComparing && (!v.content || !successorVersion?.content) && (
                                  v.contentError || successorVersion?.contentError ? (
                                    <div role="alert" style={{ padding: "8px 20px", fontSize: 12, color: theme.color.dangerText }}>
                                      The content of one of these versions could not be loaded. Hide the diff and try again.
                                    </div>
                                  ) : (
                                    <div style={{ padding: "8px 20px", fontSize: 12, color: theme.color.subtle }}>
                                      Loading content…
                                    </div>
                                  )
                                )}
                              </div>
                            );
                          })}
                        </div>
                      );
                    })()}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}
